import { getWebInstrumentations, initializeFaro } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";

import { isProductionOrStaging } from "../utils/environment";
import { isDatadog } from "../utils/isDatadog";

// faro-core keys the bundleId global on the app name, so the sourcemap
// upload script (scripts/upload-sourcemaps.mjs) must derive its --bundle-id
// global from this same string.
const appName = "reporting";

// Track production and staging.
const shouldTrackToFaro = isProductionOrStaging();

// Matches first-party Zapier URLs for trace header propagation.
const firstPartyUrls = [/https:\/\/(.*\.)?zapier(-staging)?\.com/];

// Faro already excludes its own collector. These are the other agents that
// beacon continuously on the same pages (Datadog, FullStory, Sentry). Other
// zapier.com apps on these pages may still send to Datadog;
// recording their traffic as resources inflates counts and keeps the page
// looking busy.
const otherAgentUrls = [
  /https:\/\/browser-intake-[a-z0-9-]+\.(com|eu)\//,
  /https:\/\/rs\.fullstory\.com\//,
  /https:\/\/o\d+\.ingest\.([a-z]{2}\.)?sentry\.io\//,
];

export function initializeGrafanaFaro() {
  // Don't initialize during SSR
  if (typeof window === "undefined") {
    return;
  }

  const url = process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL;
  if (!url) {
    // eslint-disable-next-line no-console
    console.info("Grafana Faro not initialized: missing collector URL.");
    return;
  }

  if (!shouldTrackToFaro) {
    // eslint-disable-next-line no-console
    console.info(
      "Grafana Faro not initialized: not in a production or staging environment."
    );
    return;
  }

  // Datadog Synthetics drives real page loads, which are not user sessions.
  if (isDatadog()) {
    // eslint-disable-next-line no-console
    console.info("Grafana Faro not initialized: synthetic user agent.");
    return;
  }

  const sampleRateOverride = Number(
    process.env.NEXT_PUBLIC_FARO_SAMPLE_RATE ?? NaN
  );

  // Must equal the --bundle-id that scripts/upload-sourcemaps.mjs uploads
  // under, or no stack trace resolves. Assigned by hand because faro-core
  // reads this only from the global its bundler plugins inject, and this
  // app's webpack build never runs them.
  const commitSha = process.env.NEXT_PUBLIC_COMMIT_SHA;
  if (commitSha) {
    (globalThis as unknown as Record<string, string>)[
      `__faroBundleId_${appName}`
    ] = commitSha;
  }

  // See https://grafana.com/docs/grafana-cloud/monitor-applications/frontend-observability/instrument/
  // for more information on what these individual parameters do.
  initializeFaro({
    url,
    app: {
      name: appName,
      environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
      version: commitSha,
    },
    // Faro takes a 0-1 fraction; 10% sampling is 0.1.
    sessionTracking: {
      samplingRate: Number.isFinite(sampleRateOverride)
        ? sampleRateOverride
        : 0.1,
    },
    ignoreUrls: otherAgentUrls,
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        // Without this, unsampled sessions send `traceparent` with the sampled
        // flag `00`, and parent-based backend samplers (the OTel default) drop
        // those requests instead of letting the backend sample them
        // independently. Needs @grafana/faro-web-tracing >= 2.11.0.
        omitTraceContextForUnsampledSessions: true,
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: firstPartyUrls,
        },
      }),
    ],
  });
}
